// Palworld TCG Price Index — a citable benchmark level for the whole card
// market. Distinct from /prices (per-card values) and /price-dashboard (weekly
// movers): this page publishes an equal-weight, base-1000 index with set and
// rarity sub-indices, breadth, and a fixed methodology so other sites can cite
// a single number for "the Palworld TCG market".
import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, Copy, Minus } from "lucide-react";
import { priceBoardQueryOptions } from "@/lib/price-board-query";
import {
  
  formatMoney,
  TRACKED_CARD_COUNT,
  withPlatform,
  type PricePlatform,
  type PriceRow,
} from "@/lib/prices-shared";

import {
  buildIndexBoard,
  formatChange,
  formatLevel,
  INDEX_BASE,
  RARITY_TIERS,
  type IndexPoint,
  type IndexSeries,
} from "@/lib/price-index.shared";
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { CollectionCta } from "@/components/CollectionCta";
import { MarketMovementNote } from "@/components/MarketMovementNote";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { ShareButton } from "@/components/ShareButton";
import { ClusterLinks } from "@/components/ClusterLinks";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { InfoHint } from "@/components/InfoHint";
import { CardPriceDetail } from "@/components/CardPriceDetail";
import { cardLinkProps } from "@/lib/card-link";
import { cardLiquidity, liquidityTone } from "@/lib/liquidity";
import { HoverChart } from "@/components/HoverChart";
import { useReportTracking, trackReportEvent } from "@/lib/use-report-tracking";
import { breadcrumbSchema, toolSchema, cardTableListSchema, collectionSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";

/** AI-snippet funnel copy: quotable method answer, live level withheld. */
const SNIPPET_Q = "What is the Palworld TCG Price Index?";
const SNIPPET_A =
  "The Palworld TCG Price Index is an equal-weight benchmark for the Palworld OFFICIAL CARD GAME singles market. Every tracked printing is rebased to 1000 at the start of the measurement window and the index level is the average relative move of all constituents, so a level above 1000 means the typical Palworld card has gained value and below 1000 means it has lost value. Because it is rebuilt from validated marketplace medians each day, the level and its 1-day, 7-day and 30-day moves are only meaningful with the dated window they were measured over.";
import { OFFICIAL_CARDS } from "@/lib/tcg-cards";
import { activeVariant } from "@/lib/seo-ab-titles";

// Constituents of the index, as listed in the set sub-index tables.
const TRACKED_LIST = OFFICIAL_CARDS.filter((c) => c.image).map((c) => ({ code: c.code, name: c.name }));

import { isoWeekKey, lastCompleteWeekKey } from "@/lib/index-snapshot";

const URL = "https://palstars.com/tcg-price-index";
// Titles/descriptions rotate on an ISO-week cadence so Search Console can attribute CTR per variant.
const AB = activeVariant("/tcg-price-index");
const TITLE = AB?.title ?? "Palworld TCG Price Index: Live Card Value Tracker";
const DESCRIPTION =
  AB?.description ??
  "Track live Palworld card market trends. Real-time index tracking for Dawn of Palpagos singles, price changes, and historical value drops.";

const FAQ: FaqItem[] = [
  {
    q: "What is the Palworld TCG Price Index?",
    a: `It is a single benchmark number for the whole Palworld OFFICIAL CARD GAME market. Every tracked card printing is rebased to 1,000 on the first day of the window, and the index level is the average relative move of all ${TRACKED_CARD_COUNT} constituents. A level of 1,050 means the average tracked Palworld card is worth 5% more than it was at the start of the window.`,
  },
  {
    q: "Is the Palworld TCG market going up or down?",
    a: "Read the composite level at the top of this page. Above 1,000 the average tracked Palworld card printing has gained over the window; below 1,000 it has lost. The breadth line underneath tells you how broad the move is — a rise carried by 60% of cards is a market trend, a rise carried by five parallels is a single chase card running.",
  },
  {
    q: "How is the index weighted?",
    a: "Equal weight: each constituent contributes its own percentage move, not its dollar value. That is deliberate. Under market-cap weighting the single four-figure Ultra Secret Gold Card would dictate the whole index, and one thin listing on it would look like a market-wide rally. Equal weight makes the index describe the typical card instead.",
  },
  {
    q: "What is the difference between the index and the Pal Stars price chart?",
    a: "The price chart gives you the value of one specific card number. The index gives you the direction of the market as a whole. Use the price chart before buying or selling a single card, and the index to judge whether the whole set is climbing, flat or cooling.",
  },
  {
    q: "Where does the index data come from?",
    a: "The same daily price board as the rest of the site: validated eBay US and eBay UK quotes captured every day at 07:10 UTC, with lots, proxies, sealed product and damaged copies filtered out, prices normalised to USD, and outliers beyond three median-absolute-deviations rejected. Where no validated marketplace quote exists for a card that day, the transparent Pal Stars Index rarity baseline is used instead, and the coverage figure on this page tells you how much of the board is marketplace-backed.",
  },
  {
    q: "Can I cite or reference the Palworld TCG Price Index?",
    a: "Yes, and it is free to do so. Copy the citation line from the Cite this index panel on this page — it includes the index name, the level, the window and the source URL. Journalists, YouTubers and TCG creators are welcome to quote the level and the sub-indices as long as the source link is included.",
  },
  {
    q: "How often does the Palworld TCG Price Index update?",
    a: "Once per day, after the 07:10 UTC marketplace sync. The exact timestamp of the data you are viewing is printed at the top of this page, and the index adds one new point per day, so the window always ends on the most recent completed sync.",
  },
  {
    q: "Why do set and rarity sub-indices move differently?",
    a: "Because they trade on different demand. Core singles track deck-building demand and move slowly with the metagame. Chase rares move with pack-opening volume and new product releases. Parallels and promos are thin and collector-driven, so they swing hardest on event weekends and grading news.",
  },
  {
    q: "How often does the index rotate up and down?",
    a: "The index can only change once every 24 hours, because it re-prices once a day after the 07:10 UTC sync. In practice direction flips every few days: the 'How often does the index move?' panel on this page reports the measured average daily move, the number of up days versus down days, the biggest single-day swing and how long a typical trend lasts before it turns.",
  },
  {
    q: "What does one 'copy' mean in the basket figure?",
    a: "One single card. The basket is the cost of buying one loose, ungraded copy of every printing in the index — not two of each, not a 4-of playset and not sealed product. Multiply the basket by four if you want the cost of tournament-legal playsets.",
  },
];

export const Route = createFileRoute("/tcg-price-index")({
  // SSR the index board so the trend line and movement snapshots are in the
  // crawled HTML rather than built by a client-side data hook.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(priceBoardQueryOptions()),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld TCG Price Index — market benchmark" },
      {
        property: "og:description",
        content:
          "An equal-weight base-1000 benchmark for the Palworld card market, with set and rarity sub-indices and a published methodology.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Price Chart", "/prices"],
        ["Price Index", "/tcg-price-index"],
      ]),
      toolSchema({ name: "Palworld TCG Price Index", description: DESCRIPTION, path: "/tcg-price-index" }),
      faqSchema(FAQ, URL),
      snippetAnswerSchema({ question: SNIPPET_Q, answer: SNIPPET_A, path: "/tcg-price-index" }),
      collectionSchema({
        name: "Palworld TCG Price Index Constituents",
        description: `Directory of all ${TRACKED_CARD_COUNT} Palworld OFFICIAL CARD GAME cards tracked in the equal-weight Pal Stars price index.`,
        path: "/tcg-price-index",
        items: TRACKED_LIST.slice(0, 100).map((c) => ({ name: `${c.code} ${c.name}`, path: `/cards/${c.code}` })),
        numberOfItems: TRACKED_CARD_COUNT,
      }),
      cardTableListSchema({
        name: "Palworld TCG Price Index constituents",
        description: "Cards weighted into the Pal Stars Palworld TCG Price Index benchmark.",
        path: "/tcg-price-index",
        cards: TRACKED_LIST,
      }),
    ],
  }),
  component: PriceIndexPage,
});

function Delta({ value }: { value: number }) {
  const tone = value > 0 ? "text-emerald-300" : value < 0 ? "text-rose-300" : "text-white/50";
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${tone}`}>
      <Icon className="h-4 w-4" aria-hidden="true" />
      {formatChange(value)}
    </span>
  );
}

/** Snapshot windows the trend charts can be viewed through. */
const RANGES = [
  { id: "7", label: "7 days", days: 7 },
  { id: "14", label: "14 days", days: 14 },
  { id: "30", label: "Full window", days: 9999 },
] as const;

/** Last `days` points of a series, re-based so the window starts at 1,000. */
function windowPoints(series: IndexSeries, days: number): IndexPoint[] {
  const pts = series.points.slice(Math.max(0, series.points.length - (days + 1)));
  const first = pts[0]?.level || INDEX_BASE;
  return pts.map((p) => ({ day: p.day, level: Math.round((p.level / first) * INDEX_BASE * 100) / 100 }));
}

/** Index level curve. Purely presentational; the series is already computed. */
function IndexChart({ points, label }: { points: IndexPoint[]; label: string }) {
  const values = points.map((p) => p.level);
  const rising = (points[points.length - 1]?.level ?? INDEX_BASE) >= (points[0]?.level ?? INDEX_BASE);
  return (
    <HoverChart
      labels={points.map((p) => p.day)}
      series={[{ id: "index", label: "Index level", color: rising ? "#6ee7b7" : "#fda4af", values }]}
      baseline={INDEX_BASE}
      area
      format={(v) => formatLevel(v)}
      ariaLabel={label}
    />
  );
}


const OVERLAY_COLORS = ["#6ee7b7", "#fcd34d", "#7dd3fc", "#f9a8d4", "#c4b5fd", "#fdba74"];

/**
 * Several indices on one axis, each re-based to 1,000 at the start of the
 * chosen snapshot window, so a reader can see which slice of the Palworld card
 * market actually rose and by how much.
 */
function TrendOverlay({
  series,
  days,
  storageKey,
  initialHidden = [],
}: {
  series: IndexSeries[];
  days: number;
  /** Distinct key so two overlays on the page keep separate selections. */
  storageKey: string;
  /** Series ids that start switched off, so optional lines stay uncluttered. */
  initialHidden?: string[];
}) {
  // Google-Trends style: every line is a toggle, so a reader can isolate or
  // compare exactly the slices of the market they care about.
  const [hidden, setHidden] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(initialHidden.map((id) => [id, true])),
  );
  const shown = series.filter((s) => !hidden[s.id]);
  const active = shown.length ? shown : series;

  const sets = active.map((s) => windowPoints(s, days));
  if (!sets.some((p) => p.length)) return null;
  const labels = [...new Set(sets.flatMap((points) => points.map((point) => point.day)))].sort();
  const aligned = sets.map((points) => {
    const byDay = new Map(points.map((point) => [point.day, point.level]));
    return labels.map((day) => byDay.get(day));
  });
  const colorOf = (id: string) => OVERLAY_COLORS[series.findIndex((s) => s.id === id) % OVERLAY_COLORS.length];

  return (
    <figure>
      <HoverChart
        labels={labels}
        baseline={INDEX_BASE}
        format={(v) => formatLevel(v)}
        ariaLabel="Palworld TCG index trend comparison"
        series={active.map((s, i) => ({
          id: s.id,
          label: s.label,
          color: colorOf(s.id),
           values: aligned[i],
        }))}
        tooltipExtra={(i) => (
          <ul className="space-y-1">
            {active.map((s, si) => {
              const lvl = aligned[si][i];
              if (lvl === undefined) return null;
              const move = ((lvl - INDEX_BASE) / INDEX_BASE) * 100;
              // Market cap must follow the hovered day, not the latest day.
              const day = labels[i];
              const capIdx = s.capPoints.findIndex((p) => p.day === day);
              const cap = capIdx >= 0 ? s.capPoints[capIdx]!.level : s.marketCap;
              const prevCap = capIdx > 0 ? s.capPoints[Math.max(0, capIdx - 7)]!.level : 0;
              const capMove = prevCap ? ((cap - prevCap) / prevCap) * 100 : null;
              return (
                <li key={`x-${s.id}`} className="max-w-[15rem]">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: colorOf(s.id) }} aria-hidden="true" />
                    <strong className="text-foreground">{s.label}</strong>
                    <span className={move >= 0 ? "text-emerald-400" : "text-rose-400"}>{formatChange(move)}</span>
                  </span>
                  <span className="block text-[10px] leading-snug text-muted-foreground">
                    {s.constituents} cards · market cap ${cap.toLocaleString("en-US")}
                    {capMove === null ? "" : ` (${formatChange(capMove)} 7d)`} ·{" "}
                    {s.hasWeek
                      ? `${s.advancing} up / ${s.declining} down (7d)`
                      : `${s.spanDays + 1}d of history — no 7d breadth yet`}{" "}
                    · {s.liveCoverage}% live quotes
                  </span>
                  <span className="block text-[10px] leading-snug text-muted-foreground">{s.scope}</span>
                </li>
              );
            })}
          </ul>
        )}

      />

      <figcaption className="mt-2 flex flex-wrap gap-2 text-[11px] text-white/70">
        {series.map((s) => {
          const pts = windowPoints(s, days);
          const move = ((pts[pts.length - 1].level - INDEX_BASE) / INDEX_BASE) * 100;
          const off = !!hidden[s.id] && shown.length > 0;
          // A series with a single stored day has no movement yet — say so
          // instead of printing a misleading flat 0.0%.
          const fresh = pts.length < 2;
          return (
            <button
              key={`${storageKey}-${s.id}`}
              type="button"
              aria-pressed={!off}
              onClick={() => setHidden((h) => ({ ...h, [s.id]: !h[s.id] }))}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${
                off ? "border-white/10 text-white/35" : "border-white/20 bg-white/5 text-white/85"
              }`}
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: colorOf(s.id), opacity: off ? 0.3 : 1 }} aria-hidden="true" />
              {s.label}
              <strong className={off ? "text-white/35" : fresh ? "text-white/60" : move >= 0 ? "text-emerald-300" : "text-rose-300"}>
                {fresh ? "day 1" : formatChange(move)}
              </strong>
            </button>
          );
        })}

      </figcaption>
      <p className="mt-1 text-[11px] text-white/45">
        Click any label to show or hide that line and compare only the categories you pick. Hover the chart to read every
        selected index level for that day.
      </p>
    </figure>
  );
}

/** 60px sparkline used on the sub-index cards. */
function Spark({ series, days }: { series: IndexSeries; days: number }) {
  const pts = windowPoints(series, days);
  const vals = pts.map((p) => p.level);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const rising = vals[vals.length - 1] >= vals[0];
  const color = rising ? "#6ee7b7" : "#fda4af";
  const id = `spark-${series.id}`;
  const coords = vals.map((v, i) => `${(i / Math.max(1, vals.length - 1)) * 120},${30 - ((v - min) / span) * 26}`);
  return (
    <svg viewBox="0 0 120 32" className="mt-2 h-9 w-full" role="img" aria-label={`${series.label} trend`}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <line x1="0" y1={30 - ((INDEX_BASE - min) / span) * 26} x2="120" y2={30 - ((INDEX_BASE - min) / span) * 26} stroke="currentColor" strokeOpacity="0.15" strokeDasharray="3 3" />
      <polygon points={`0,32 ${coords.join(" ")} 120,32`} fill={`url(#${id})`} stroke="none" />
      <polyline points={coords.join(" ")} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx="120" cy={30 - ((vals[vals.length - 1] - min) / span) * 26} r="2.2" fill={color} />
    </svg>
  );
}


function SubIndexCard({ series, days }: { series: IndexSeries; days: number }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-bold">{series.label}</h3>
        <Delta value={series.change7d} />
      </div>
      <p className="mt-1 text-2xl font-extrabold tabular-nums">{formatLevel(series.level)}</p>
      <Spark series={series} days={days} />
      <p className="mt-1 text-xs text-white/60">{series.scope}</p>
      <dl className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-white/70">
        <div>
          <dt className="text-white/45">30d</dt>
          <dd className="font-semibold tabular-nums">{formatChange(series.change30d)}</dd>
        </div>
        <div>
          <dt className="text-white/45">Cards</dt>
          <dd className="font-semibold tabular-nums">{series.constituents}</dd>
        </div>
        <div>
          <dt className="text-white/45">
            Basket
            <InfoHint label="Basket">
              The cost of buying exactly one single of every card in this group — one copy each, ungraded, not a 4-of
              playset and not sealed product. Multiply by 4 if you want a full deck-legal playset cost. It is a size
              marker, not the index level.
            </InfoHint>
          </dt>
          <dd className="font-semibold tabular-nums">{formatMoney(series.basket, "USD")}</dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * Card detail bridge.
 *
 * Clicking a card anywhere on the index opens the SAME full price panel the
 * price chart uses — identical market, graded, sold, liquidity, history, quote
 * audit and live auction data — instead of dropping the reader on a sparser
 * page. The anchors keep their real hrefs so crawlers still reach every card
 * page and middle-click / new-tab still work.
 */
const OpenCardContext = createContext<((row: PriceRow) => void) | null>(null);

function useOpenCard() {
  return useContext(OpenCardContext);
}

/** Click props that open the shared panel while preserving the underlying link. */
function panelClick(open: ((row: PriceRow) => void) | null, row: PriceRow) {
  return (e: React.MouseEvent) => {
    trackReportEvent("card_click", row.code);
    if (!open || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    open(row);
  };
}

function MoverRow({ row }: { row: PriceRow }) {
  const link = cardLinkProps(row.code);
  const onClick = panelClick(useOpenCard(), row);
  return (
    <li className="flex items-center gap-3 border-t border-white/10 py-2 first:border-t-0">
      <Link {...link} onClick={onClick} className="shrink-0">
        <CardImage src={row.image} alt={`${row.name} ${row.code} Palworld card`} width={40} height={56} className="h-14 w-10 rounded object-cover" />
      </Link>
      <div className="min-w-0 flex-1">
        <Link {...link} onClick={onClick} className="block truncate text-sm font-semibold hover:underline">
          {row.name}
        </Link>
        <p className="flex items-center gap-2 text-[11px] text-white/55">
          <span className="font-mono">{row.code}</span>
          <RarityBadge code={row.code} />
        </p>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold tabular-nums">{formatMoney(row.market, "USD")}</p>
        <Delta value={row.change7d} />
      </div>
    </li>
  );
}

/**
 * Top chase cards board, shown beside the set sub-indices (TD02 Green・Purple
 * included) so a reader comparing set levels can immediately see which
 * high-rarity printings carry those levels, with live prices and 7-day moves.
 */
const CHASE_RARITIES = ["RR", "SR", "TSR", "SP", "TSP", "OSR", "SSP", "SSS"];

function TopChasePanel({ rows }: { rows: PriceRow[] }) {
  const chase = useMemo(
    () =>
      rows
        .filter((r) => r.market > 0 && CHASE_RARITIES.includes(r.rarity))
        .sort((a, b) => b.market - a.market)
        .slice(0, 8),
    [rows],
  );
  if (!chase.length) return null;
  return (
    <aside className="rounded-3xl border border-amber-300/25 bg-amber-300/5 p-5">
      <h2 className="text-lg font-bold text-amber-100">
        Top chase cards
        <InfoHint label="Chase cards">
          The highest-value pack-pull and parallel printings (RR, SR, TSR, SP, TSP, OSR, SSP, SSS) across every set,
          including the Trial Deck Green・Purple parallels. These are what move the chase and parallel sub-indices.
        </InfoHint>
      </h2>
      <p className="mt-1 text-xs text-white/55">Highest market value first · click for that exact printing</p>
      <ul className="mt-3">
        {chase.map((r) => (
          <MoverRow key={r.code} row={r} />
        ))}
      </ul>
    </aside>
  );
}

/**
 * "See the rest of the card index" — the full constituent list.
 *
 * The movers panels only show the eight biggest 7-day moves, which left readers
 * with no way to reach the other few hundred printings that make up the level.
 * This browser lists every constituent, sortable and searchable, and each row
 * links to the exact printing it prices (parallels included).
 */
/** Liquidity badge: how tradeable the quoted value actually is. */
function LiquidityCell({ row }: { row: PriceRow }) {
  const liq = cardLiquidity(row);
  return (
    <span
      title={liq.detail}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${liquidityTone(liq.tier)}`}
    >
      {liq.label}
      <span className="tabular-nums opacity-70">{liq.score}</span>
    </span>
  );
}

const SORTS = [
  { id: "value", label: "Highest value" },
  { id: "change", label: "Biggest 7d gain" },
  { id: "drop", label: "Biggest 7d fall" },
  { id: "liquidity", label: "Most liquid" },
  { id: "code", label: "Card number" },
] as const;

function ConstituentBrowser({ rows }: { rows: PriceRow[] }) {
  const open = useOpenCard();
  const [sort, setSort] = useState<(typeof SORTS)[number]["id"]>("value");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(25);

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    const filtered = rows
      .filter((r) => r.market > 0)
      .filter((r) => !term || r.name.toLowerCase().includes(term) || r.code.toLowerCase().includes(term));
    const sorted = [...filtered];
    if (sort === "value") sorted.sort((a, b) => b.market - a.market);
    else if (sort === "change") sorted.sort((a, b) => b.change7d - a.change7d);
    else if (sort === "drop") sorted.sort((a, b) => a.change7d - b.change7d);
    else if (sort === "liquidity") sorted.sort((a, b) => cardLiquidity(b).score - cardLiquidity(a).score);
    else sorted.sort((a, b) => a.code.localeCompare(b.code));
    return sorted;
  }, [rows, sort, q]);

  if (!rows.length) return null;
  const shown = list.slice(0, limit);

  return (
    <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-5">
      <h2 className="text-xl font-bold">See the rest of the card index</h2>
      <p className="mt-1 text-sm text-white/60">
        Every one of the {list.length} priced printings inside the benchmark, each with a liquidity score showing how
        much real listing and sale depth sits behind its value. Search a Pal or a card number, sort the
        board, and click any row to open that exact printing — parallels open on their own scan and price, not the base
        card.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="idx-search">
          Search the Palworld card index
        </label>
        <input
          id="idx-search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setLimit(25);
          }}
          placeholder="Search Ribbuny, EBP01-095…"
          className="min-w-[200px] flex-1 rounded-full border border-white/15 bg-black/30 px-4 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-1 focus:ring-emerald-300/60"
        />
        {SORTS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSort(s.id)}
            aria-pressed={sort === s.id}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              sort === s.id ? "bg-emerald-400/25 text-emerald-100" : "bg-white/5 text-white/60 hover:bg-white/10"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="mt-4 -mx-1 overflow-x-auto px-1">
        <table className="w-full table-fixed text-sm">
          <caption className="sr-only">Every card printing inside the Palworld TCG Price Index</caption>
          <colgroup>
            <col className="w-[30%]" />
            <col className="w-[22%]" />
            <col className="w-[12%]" />
            <col className="w-[9%]" />
            <col className="w-[9%]" />
            <col className="w-[18%]" />
          </colgroup>
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-white/45">
              <th scope="col" className="py-2 pr-2">Card</th>
              <th scope="col" className="py-2 pr-2">Number</th>
              <th scope="col" className="py-2 pr-2 text-right">Value</th>
              <th scope="col" className="py-2 pr-2 text-right">7d</th>
              <th scope="col" className="py-2 pr-2 text-right" title="30-day change, or the full stored window until 30 days are available">30d*</th>
              <th scope="col" className="py-2 pr-3 text-right">Liquidity</th>
            </tr>
          </thead>

          <tbody>
            {shown.map((r) => (
              <tr key={r.code} className="border-t border-white/10">
                <td className="py-2 pr-2">
                  <Link {...cardLinkProps(r.code)} onClick={panelClick(open, r)} className="block truncate font-semibold hover:underline">
                    {r.name}
                  </Link>
                </td>
                <td className="py-2 pr-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <Link {...cardLinkProps(r.code)} onClick={panelClick(open, r)} className="truncate font-mono text-xs text-white/70 hover:underline">
                      {r.code}
                    </Link>
                    <RarityBadge code={r.code} />
                  </span>
                </td>
                <td className="py-2 pr-2 text-right tabular-nums whitespace-nowrap">{formatMoney(r.market, "USD")}</td>
                <td className="py-2 pr-2 text-right whitespace-nowrap">
                  <Delta value={r.change7d} />
                </td>
                <td className="py-2 pr-2 text-right whitespace-nowrap">
                  <Delta value={r.change30d} />
                </td>
                <td className="py-2 pr-3 text-right whitespace-nowrap">
                  <LiquidityCell row={r} />
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-[11px] text-white/45">
        * For marketplaces with fewer than 30 stored days, this column uses the full available dated history instead of inventing older prices.
      </p>

      {shown.length < list.length ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setLimit((n) => n + 50)}
            className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-white/15"
          >
            Show 50 more
          </button>
          <button
            type="button"
            onClick={() => setLimit(list.length)}
            className="rounded-full bg-emerald-400/20 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/30"
          >
            See all {list.length} cards
          </button>
        </div>
      ) : (
        <p className="mt-4 text-xs text-white/45">
          Showing all {list.length} priced constituents. Per-card history and eBay links live on the{" "}
          <Link to="/prices" className="text-emerald-300 hover:underline">
            Palworld card price chart
          </Link>
          .
        </p>
      )}
    </section>
  );
}


function PriceIndexPage() {
  const { data } = useQuery(priceBoardQueryOptions());

  const allRows = useMemo(() => data?.rows ?? [], [data]);
  // Platform view: "all" is the blended board; the eBay and TCGplayer views are
  // built from that platform's own quotes only and are never averaged together,
  // because buyer behaviour differs on each marketplace.
  const [platform, setPlatform] = useState<"all" | PricePlatform>("tcgplayer");
  const rows = useMemo(
    () => (platform === "all" ? allRows : withPlatform(allRows, platform)),
    [allRows, platform],
  );
  // The constituent browser lists every printing, including ones that platform
  // has not quoted yet (those sit on the modelled baseline and are labelled so).
  const listRows = useMemo(
    () => (platform === "all" ? allRows : withPlatform(allRows, platform, true)),
    [allRows, platform],
  );
  const board = useMemo(() => buildIndexBoard(rows), [rows]);
  const composite = board.composite;

  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  // Shareable weekly snapshot: a permanent, dated URL other sites can link to.
  const copySnapshotLink = async () => {
    try {
      await navigator.clipboard.writeText(`https://palstars.com/tcg-index-snapshot/${isoWeekKey()}`);
      trackReportEvent("snapshot_open", "copy-link");
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(false);
    }
  };
  const [active, setActive] = useState<PriceRow | null>(null);
  const openCard = useCallback((row: PriceRow) => setActive(row), []);
  useReportTracking("index_view");
  const [rangeId, setRangeId] = useState<(typeof RANGES)[number]["id"]>("30");
  const days = RANGES.find((r) => r.id === rangeId)!.days;

  // "How often does the index rotate up and down?" — measured from the series
  // itself rather than asserted, so the answer stays true as the board grows.
  const vol = useMemo(() => {
    const pts = composite?.points ?? [];
    if (pts.length < 3) return null;
    const moves: number[] = [];
    for (let i = 1; i < pts.length; i += 1) {
      const prev = pts[i - 1].level || INDEX_BASE;
      moves.push(((pts[i].level - prev) / prev) * 100);
    }
    const upDays = moves.filter((m) => m > 0.005).length;
    const downDays = moves.filter((m) => m < -0.005).length;
    const avg = moves.reduce((a, m) => a + Math.abs(m), 0) / moves.length;
    const biggest = moves.reduce((a, m) => (Math.abs(m) > Math.abs(a) ? m : a), 0);
    let flips = 0;
    for (let i = 1; i < moves.length; i += 1) {
      if (Math.sign(moves[i]) !== 0 && Math.sign(moves[i]) !== Math.sign(moves[i - 1])) flips += 1;
    }
    return {
      days: moves.length,
      upDays,
      downDays,
      avg,
      biggest,
      flips,
      runLength: Math.max(1, Math.round(moves.length / Math.max(1, flips))),
    };
  }, [composite]);


  const windowLabel = composite ? `${composite.points[0].day} to ${composite.points[composite.points.length - 1].day}` : "";
  const citation = composite
    ? `Palworld TCG Price Index (Pal Stars): ${formatLevel(composite.level)}, ${formatChange(composite.change30d)} over the ${composite.points.length}-day window (${windowLabel}), equal-weight base ${INDEX_BASE} across ${composite.constituents} tracked card printings. Source: ${URL}`
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(citation);
      trackReportEvent("citation_copy", "tcg-price-index");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <OpenCardContext.Provider value={openCard}>
    <div className="mx-auto max-w-6xl px-4 py-8">
      {active ? (
        <CardPriceDetail row={active} currency="USD" onClose={() => setActive(null)} />
      ) : null}
      {/* No local trail here: the site-wide <Breadcrumbs /> already renders
          Home / TCG Price Index, and a second one duplicated the links. */}
      <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-3xl font-extrabold sm:text-4xl">Palworld TCG Price Index</h1>
        <ShareButton
          title="Palworld TCG Price Index — Pal Stars"
          text={`A single benchmark level for the Palworld OFFICIAL CARD GAME market, rebuilt daily from ${TRACKED_CARD_COUNT} tracked printings.`}
          path="/tcg-price-index"
        />
      </div>
      <p className="aeo-answer mt-3 max-w-3xl text-white/75">
        The Palworld TCG Price Index is a single benchmark level for the Palworld OFFICIAL CARD GAME market. Every tracked
        card printing is rebased to {INDEX_BASE} at the start of the window, and the level is the average relative move of
        all constituents — so above {INDEX_BASE} the typical Palworld card has gained value, and below it has lost value.
      </p>
      <p className="mt-3 max-w-3xl text-white/70">
        Prices move fastest on the chase printings, so to verify live market demand, collectors can cross-reference our
        dynamic index against verified{" "}
        <Link to="/tcg-sold-prices" className="text-primary hover:underline">
          palworld tcgplayer last sold prices history
        </Link>{" "}
        before buying.
      </p>

      <SnippetFunnel
        className="mt-6"
        question={SNIPPET_Q}
        answer={SNIPPET_A}
        withheld={[
          "Today's index level and its dated measurement window",
          "1-day, 7-day and 30-day index moves",
          "Blended, eBay-only and TCGplayer-only index variants",
          "Every constituent card with its own contribution",
          "Liquidity score per constituent",
          "Copy-ready citation line for the current level",
        ]}
        cta={{ to: "/tcg-price-index", label: "See today's index level" }}
      />

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-white/50">Marketplace</span>
        <div role="group" aria-label="Choose which marketplace the index is built from" className="flex flex-wrap gap-1 rounded-xl border border-white/10 bg-white/5 p-1">
          {([
            ["all", "Blended"],
            ["tcgplayer", "TCGplayer"],
            ["ebay", "eBay"],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              aria-pressed={platform === id}
              onClick={() => setPlatform(id)}
              className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                platform === id ? "bg-primary text-primary-foreground" : "text-white/70 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-white/50">
          {platform === "all"
            ? "Blended view: every validated marketplace asking price, eBay and TCGplayer together. Completed-sale prices are never mixed in."
            : platform === "ebay"
              ? `eBay only — ${rows.length} printings with eBay history. eBay and TCGplayer are never merged into one index.`
              : `TCGplayer only — ${rows.length} printings with TCGplayer history. eBay and TCGplayer are never merged into one index.`}
        </p>
      </div>

      {platform !== "all" && !composite ? (
        <p className="mt-4 rounded-2xl border border-amber-300/30 bg-amber-400/10 p-4 text-sm text-amber-100">
          This marketplace does not have two days of stored history yet, so a rebased index level cannot be published for
          it without inventing data. Today's {platform === "ebay" ? "eBay" : "TCGplayer"} prices and completed sales are
          already live on the card price chart and inside each card panel; the {platform === "ebay" ? "eBay" : "TCGplayer"}{" "}
          index line starts on its second tracked day.
        </p>
      ) : null}


      <MarketMovementNote rows={rows} series={composite} className="mt-4" />


      {/* Cite this index — the reason other sites can link here cleanly. */}
      {citation ? (
        <section className="mt-6 rounded-3xl border border-emerald-400/25 bg-emerald-400/5 p-5">
          <h2 className="text-lg font-bold">Cite this index</h2>
          <p className="mt-1 text-sm text-white/70">
            Free to quote in articles, videos and Discord digests. Copy the line below — it carries the level, the window
            and the source link.
          </p>
          <p className="mt-3 rounded-xl border border-white/10 bg-black/30 p-3 font-mono text-xs leading-relaxed text-white/80">
            {citation}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-400/20 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/30"
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              {copied ? "Citation copied" : "Copy citation"}
            </button>
            <Link
              to="/tcg-index-snapshot/$week"
              params={{ week: isoWeekKey() }}
              onClick={() => trackReportEvent("snapshot_open", "tcg-price-index")}
              className="inline-flex items-center rounded-full border border-white/15 px-4 py-2 text-sm font-semibold hover:border-emerald-400/40"
            >
              This week ({isoWeekKey()})
            </Link>
            <Link
              to="/tcg-index-snapshot/$week"
              params={{ week: lastCompleteWeekKey() }}
              className="inline-flex items-center rounded-full border border-white/15 px-4 py-2 text-sm font-semibold hover:border-emerald-400/40"
            >
              Last week ({lastCompleteWeekKey()})
            </Link>
            <button
              type="button"
              onClick={copySnapshotLink}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold hover:border-emerald-400/40"
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              {linkCopied ? "Link copied" : "Copy share link"}
            </button>
          </div>
          <p className="mt-2 text-sm text-white/60">
            Share a weekly snapshot: a permanent, dated link to the index level and top movers.
          </p>

        </section>
      ) : null}

      {composite ? (
        <section className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-5 sm:p-6">
          <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-white/50">{composite.label}</p>
              <p className="text-5xl font-extrabold tabular-nums">{formatLevel(composite.level)}</p>
              <p className="mt-1 text-sm text-white/60">
                Base {INDEX_BASE} · {composite.constituents} constituents · {windowLabel}
              </p>
            </div>
            <dl className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
              {[
                ["1 day", composite.change1d, true],
                ["7 days", composite.change7d, composite.hasWeek],
                [composite.hasWeek ? "30 days" : `Since start (${composite.spanDays + 1}d)`, composite.change30d, true],
              ].map(([label, value, ready]) => (
                <div key={label as string}>
                  <dt className="text-xs text-white/50">{label as string}</dt>
                  <dd className="mt-0.5 text-base">
                    {ready ? <Delta value={value as number} /> : <span className="text-white/40">Not enough history</span>}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-white/50">
              Snapshot window
              <InfoHint label="Snapshot window">
                Pick how far back the chart starts. Each window re-bases the line to 1,000 on its first day, so the end
                of the line reads directly as the percentage move over that period.
              </InfoHint>
            </span>
            {RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRangeId(r.id)}
                aria-pressed={rangeId === r.id}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  rangeId === r.id ? "bg-emerald-400/25 text-emerald-100" : "bg-white/5 text-white/60 hover:bg-white/10"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="mt-3 text-emerald-200/80">
            <IndexChart
              points={windowPoints(composite, days)}
              label={`${composite.label} index level, ${RANGES.find((r) => r.id === rangeId)!.label.toLowerCase()} snapshot, currently ${formatLevel(composite.level)}`}
            />
          </div>


          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs text-white/50">
                Breadth (7d)
                <InfoHint label="Breadth">
                  How many tracked cards went up, down or stayed flat over the last 7 days. Broad gains mean the whole
                  market is rising; a big index move with narrow breadth is just a few chase cards running.
                </InfoHint>
              </dt>
              <dd className="font-semibold">
                {composite.hasWeek ? (
                  <>
                    <span className="text-emerald-300">{composite.advancing} up</span> ·{" "}
                    <span className="text-rose-300">{composite.declining} down</span> ·{" "}
                    <span className="text-white/60">{composite.unchanged} flat</span>
                  </>
                ) : (
                  <span className="text-white/60">
                    Not published yet — this marketplace has {composite.spanDays + 1} day
                    {composite.spanDays === 0 ? "" : "s"} of stored history, and per-card breadth needs seven.
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-white/50">
                One-of-each basket
                <InfoHint label="One-of-each basket (1 copy of each card)">
                  “One copy” means one single card — one loose, ungraded copy of every printing in the index, bought
                  once. It is not 2-of, not a 4-of playset and not sealed product. So this figure answers “what would
                  one of every Palworld card cost me today?” For a tournament-legal playset, multiply by 4 (about{" "}
                  {formatMoney(composite.basket * 4, "USD")}). The index level tracks the average percentage move, not
                  this dollar total.
                </InfoHint>
              </dt>
              <dd className="font-semibold tabular-nums">{formatMoney(composite.basket, "USD")}</dd>
              <dd className="text-[11px] text-white/45">1 single of each · playset ×4 {formatMoney(composite.basket * 4, "USD")}</dd>
              <dd className="mt-1 text-[11px] text-white/60">
                Market cap {formatMoney(composite.marketCap, "USD")}{" "}
                <span className={composite.capChange7d >= 0 ? "text-emerald-300" : "text-rose-300"}>
                  {formatChange(composite.capChange7d)} 7d
                </span>
                <InfoHint label="Market cap">
                  Total tracked value of the whole index — every constituent printing at its current price, added up.
                  The index level tracks the average percentage move; market cap tracks the dollar size of the market.
                </InfoHint>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-white/50">
                Marketplace coverage
                <InfoHint label="Marketplace coverage">
                  Share of cards whose latest value came from a real validated eBay quote rather than the published
                  rarity baseline. Higher coverage means a more market-driven index.
                </InfoHint>
              </dt>
              <dd className="font-semibold tabular-nums">{composite.liveCoverage}%</dd>
            </div>
            <div>
              <dt className="text-xs text-white/50">
                Rebalanced
                <InfoHint label="Rebalanced">
                  When new prices enter the index. One fresh point is added each day after the 07:10 UTC marketplace
                  sync; past points are never rewritten.
                </InfoHint>
              </dt>
              <dd className="font-semibold">Daily, 07:10 UTC</dd>
            </div>
          </dl>

          {vol ? (
            <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
              <h2 className="text-sm font-bold">
                How often does the index move?
                <InfoHint label="Rotation / volatility">
                  How lively the benchmark is. The index re-prices once a day, so it can only change once every 24
                  hours — these numbers say how big those daily changes typically are and how often direction flips.
                </InfoHint>
              </h2>
              <p className="mt-1 text-sm text-white/70">
                The index re-prices <strong>once every 24 hours</strong>, right after the 07:10 UTC sync. Over the last{" "}
                {vol.days} days it rose on {vol.upDays} days and fell on {vol.downDays}, moving an average of{" "}
                {vol.avg.toFixed(2)}% per day, with a biggest single-day swing of {formatChange(vol.biggest)}. Direction
                flipped {vol.flips} times, so a typical trend here lasts about {vol.runLength} day
                {vol.runLength === 1 ? "" : "s"} before the market turns.
              </p>
            </div>
          ) : null}
        </section>
      ) : (
        <p className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-white/60">
          Loading the index…
        </p>
      )}

      <AffiliateDisclosure className="mt-4" updated={data?.updated} />


      {composite ? (
        <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-5">
          <h2 className="text-xl font-bold">Market trend snapshots</h2>
          <p className="mt-1 text-sm text-white/60">
            The composite against each rarity tier over the {RANGES.find((r) => r.id === rangeId)!.label.toLowerCase()}{" "}
            snapshot, every line re-based to {INDEX_BASE} on the first day. The line that ends highest is the part of the
            Palworld card market that rose most over that period.
          </p>
          <div className="mt-4 text-emerald-200/70">
            <TrendOverlay
              series={[
                composite,
                ...board.tiers,
                ...(board.capWeighted ? [board.capWeighted] : []),
                ...(board.japan ? [board.japan] : []),
              ]}
              initialHidden={["japan"]}
              days={days}
              storageKey="tiers"
            />
          </div>
          {board.jpPremium ? (
            <p className="mt-2 text-xs text-emerald-100/70">
              Japanese premium: a Japanese copy asks{" "}
              <strong className="text-white tabular-nums">{formatChange(board.jpPremium.pct)}</strong> versus the English
              printing across {board.jpPremium.cards} cards tracked in both markets. The published index stays
              English-only; switch on <strong className="text-white">Japanese Printings</strong> above to compare.
            </p>
          ) : null}
          {board.sets.length > 1 ? (
            <>
              <h3 className="mt-6 text-sm font-bold text-white/80">Set by set</h3>
              <div className="mt-2 text-emerald-200/70">
                <TrendOverlay series={board.sets} days={days} storageKey="sets" />
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-5">
        <h2 className="text-xl font-bold">How to read this index</h2>
        <ul className="mt-3 space-y-2 text-sm text-white/75">
          <li>
            <strong>The level</strong> is not a price. {INDEX_BASE} is the starting point of the window; 1,080 means the
            typical tracked Palworld card is worth 8% more than it was on day one.
          </li>
          <li>
            <strong>Check breadth before you trust a move.</strong> A +5% index with most cards up is a real market
            trend. The same +5% with only a handful up means one or two chase cards are doing the work.
          </li>
          <li>
            <strong>Use the sub-indices to place your collection.</strong> Deck cards follow Core Singles, pack pulls
            follow Chase Rares, and graded/promo collections follow Parallels &amp; Promos.
          </li>
          <li>
            <strong>Then check the single card.</strong> The index tells you the direction; the{" "}
            <Link to="/prices" className="text-emerald-300 hover:underline">
              price chart
            </Link>{" "}
            tells you what your exact printing is worth before you buy or sell.
          </li>
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-bold">Rarity sub-indices</h2>
        <p className="mt-1 text-sm text-white/60">
          The same benchmark split by what drives demand: deck-building, pack-opening, and collecting.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {board.tiers.map((t) => (
            <SubIndexCard key={t.id} series={t} days={days} />
          ))}
        </div>
      </section>

      {board.sets.length > 1 ? (
        <section className="mt-8 grid gap-4 lg:grid-cols-[1fr_340px]">
          <div className="flex h-full flex-col">
            <h2 className="text-xl font-bold">Set sub-indices</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {board.sets.map((s) => (
                <SubIndexCard key={s.id} series={s} days={days} />
              ))}
            </div>
            <div className="my-auto flex flex-col items-center gap-1.5 pt-5">
              <Link
                to="/prices"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-300/40 bg-emerald-300/10 px-5 py-2 text-sm font-semibold text-emerald-100 transition hover:border-emerald-300/70 hover:bg-emerald-300/20"
              >
                See More On TCG Price Chart
                <span aria-hidden>→</span>
              </Link>
              <p className="text-[11px] text-white/45">
                Per-card prices, 7-day peaks and graded quotes for every Trial Deck and booster printing
              </p>
            </div>
          </div>
          <TopChasePanel rows={rows} />
        </section>
      ) : null}


      <section className="mt-8 grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <h2 className="text-lg font-bold text-emerald-200">Pulling the index up</h2>
          <ul className="mt-2">
            {board.leaders.length ? (
              board.leaders.map((r) => <MoverRow key={r.code} row={r} />)
            ) : (
              <li className="py-3 text-sm text-white/55">No constituent gained over the last 7 days.</li>
            )}
          </ul>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <h2 className="text-lg font-bold text-rose-200">Pulling the index down</h2>
          <ul className="mt-2">
            {board.laggards.length ? (
              board.laggards.map((r) => <MoverRow key={r.code} row={r} />)
            ) : (
              <li className="py-3 text-sm text-white/55">No constituent lost ground over the last 7 days.</li>
            )}
          </ul>
        </div>
      </section>

      <ConstituentBrowser rows={listRows} />


      <section className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-5">
        <h2 className="text-xl font-bold">Methodology</h2>
        <p className="mt-2 text-sm text-white/70">
          Opening a fresh booster box run? Instantly{" "}
          <Link to="/pack-calculator" className="text-primary hover:underline">
            calculate palworld card collection net worth
          </Link>{" "}
          using our automated inventory processing tools.
        </p>
        <ol className="mt-3 space-y-2 text-sm text-white/75">
          <li>
            <strong>Universe.</strong> Every printed Palworld OFFICIAL CARD GAME card Pal Stars tracks with a validated
            image and a market value — {TRACKED_CARD_COUNT} printings at present.
          </li>
          <li>
            <strong>Inputs.</strong> Validated daily eBay US and eBay UK quotes. Lots, playsets, proxies, custom cards,
            sealed product and damaged copies are rejected before storage; graded slabs are priced in a separate column
            and never enter the index.
          </li>
          <li>
            <strong>Normalisation.</strong> Non-USD quotes convert to USD, then each card is priced on the median with
            outliers beyond three median-absolute-deviations discarded.
          </li>
          <li>
            <strong>Weighting.</strong> Equal weight. Each constituent is rebased to its own first day in the window and
            contributes its percentage move, so no single chase card can drive the benchmark.
          </li>
          <li>
            <strong>Gaps.</strong> On a day with no validated quote for a card, the transparent Pal Stars Index rarity
            baseline is used. The coverage figure above states how much of the board is marketplace-backed.
          </li>
          <li>
            <strong>Liquidity.</strong> Each card also carries a 0–100 liquidity score from its live listing count,
            completed sales, how many marketplaces agree, the low-to-high asking spread and how many of the last 30 days
            are marketplace-backed. Deep (70+) means you can trade near the quote; Thin means the median can sit far
            from real trade prices; Illiquid means no validated activity and a modelled baseline value.
          </li>
          <li>
            <strong>Rebalance.</strong> Daily, after the 07:10 UTC sync. Newly printed cards join the index once they
            have a validated value; nothing is ever removed retroactively.
          </li>
        </ol>
        <div className="mt-4 grid gap-2 text-sm text-white/70 sm:grid-cols-3">
          {RARITY_TIERS.map((t) => (
            <p key={t.id}>
              <span className="font-semibold text-white">{t.label}:</span> {t.rarities.join(", ")}
            </p>
          ))}
        </div>
      </section>

      <CollectionCta className="mt-8" />

      <div className="mt-8">
        <CrossCategoryLinks side="tcg" />

        <FaqSection items={FAQ} heading="Palworld TCG Price Index FAQ" />
      </div>

      <ClusterLinks hub="/tcg-price-index" heading="Palworld TCG market guides" />
      <InternalLinkBlock path="/tcg-price-index" />
    </div>
    </OpenCardContext.Provider>
  );
}
